/**
 * Las dos "caras" de un anuncio: piscina y jardín.
 *
 * Un mismo anuncio puede alquilarse como piscina y como jardín, con título,
 * descripción, tramos de precio y orden de fotos propios de cada cara. No son dos
 * anuncios: Sharetribe guarda una sola entidad, así que la cara de piscina son los
 * campos de siempre (`title`, `description`, `publicData.priceRange`, `images`) y la
 * de jardín es una capa de campos `garden*` por encima.
 *
 * De ahí salen las tres reglas de retrocompatibilidad que gobiernan este módulo:
 *
 *   - Un anuncio de jardín de los que ya existen (`searchListingType === 'garden'`)
 *     tiene sus datos de jardín en los campos base. Es jardín y no tiene piscina, y
 *     los campos `garden*` no se miran.
 *   - Un anuncio de piscina sin `hasGarden` se comporta exactamente como hoy.
 *   - Cualquier campo `garden*` que falte cae al valor de la cara de piscina, de modo
 *     que activar el jardín nunca deja la ficha a medias.
 *
 * La cara `POOL_FACE` es, por tanto, "los campos base del anuncio". Para un anuncio
 * de interiores es la única que existe, y por eso es también la cara que se resuelve
 * ahí: no significa que tenga piscina, significa que no hay una segunda versión.
 *
 * El espejo de servidor (server/api-util/listingFaces.js) cubre solo la parte de
 * precio, que es la que decide dinero.
 */

import { GARDEN, LISTING_STATE_CLOSED, LISTING_STATE_DRAFT, POOL } from './types';
import { parse } from './urlHelpers';
import { stringifyDateToISO8601, timestampToDate } from './dates';
import { isManualReservation } from './bookingTimeline';

export const POOL_FACE = POOL;
export const GARDEN_FACE = GARDEN;
/**
 * Lo que se guarda como cara de un anuncio de interiores (`protectedData.bookingFace`).
 * Internamente su cara es `POOL_FACE`, pero ese nombre no debe llegar a los datos
 * guardados: quien los lea tomaría un interior por una piscina. Al leerla, todo lo
 * que no es `GARDEN_FACE` es la cara base, así que se cobra y se pinta igual.
 */
export const INDOOR_FACE = 'indoor';

/**
 * Ventana de temporada de piscina (mes-día, ambos inclusive) para una fecha buscada o
 * reservada. Fuera de ella la cara que se muestra por defecto ante una fecha es la de
 * jardín.
 */
export const SUMMER_START_MONTH_DAY = '05-01';
export const SUMMER_END_MONTH_DAY = '09-30';

/**
 * Sin fecha decide hoy, con una ventana más ancha: desde marzo la gente ya busca piscina
 * para el verano, aunque aún no haya elegido día.
 */
export const NO_DATE_SEASON_START_MONTH_DAY = '03-01';

/** Meses (1–12) en los que, por defecto, un anuncio dual se ofrece como piscina. */
export const DEFAULT_POOL_MONTHS = [5, 6, 7, 8, 9];

const MONTH_INDEX_FROM_YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Normaliza la lista de meses de piscina que guarda el anfitrión.
 * @returns {number[]} meses 1–12 únicos y ordenados; vacío si no hay lista usable
 */
export const normalizePoolMonths = months => {
  if (!Array.isArray(months)) {
    return [];
  }
  const cleaned = [
    ...new Set(
      months
        .map(m => (typeof m === 'number' ? m : parseInt(m, 10)))
        .filter(m => Number.isInteger(m) && m >= 1 && m <= 12)
    ),
  ].sort((a, b) => a - b);
  return cleaned;
};

/**
 * ¿La fecha cae en un mes de piscina para este anuncio?
 * Con `poolMonths` del anfitrión manda esa lista; si no hay, la ventana de verano global.
 */
export const isPoolSeasonDate = (ymd, poolMonths) => {
  const match = MONTH_INDEX_FROM_YMD.exec(String(ymd || '').trim());
  if (!match) {
    return false;
  }
  const normalized = normalizePoolMonths(poolMonths);
  if (normalized.length > 0) {
    return normalized.includes(parseInt(match[2], 10));
  }
  return isSummerDate(ymd);
};

/**
 * Aplica un % de descuento a cada tramo de `priceRange` (importes en subunidades).
 * @returns {Array|null} nuevos tramos, o null si el % no es usable
 */
export const applyGardenDiscountToPriceRange = (priceRange, percent) => {
  const pct = Number(percent);
  if (!Number.isFinite(pct) || pct < 0 || pct >= 100 || !Array.isArray(priceRange)) {
    return null;
  }
  const factor = (100 - pct) / 100;
  const next = priceRange
    .filter(row => row && row.quantityRange && row.amount != null)
    .map(row => ({
      quantityRange: row.quantityRange,
      amount: Math.max(0, Math.round(Number(row.amount) * factor)),
      currency: row.currency || 'EUR',
    }));
  return next.length > 0 ? next : null;
};

/**
 * ¿Siguen los precios de jardín tal como los calculó el % de descuento? Es decir, ¿son
 * exactamente `applyGardenDiscountToPriceRange(piscina, %)`? Si sí, nadie los ha tocado a mano
 * en Precios y se pueden recalcular al cambiar el %; si no, se respetan. Sin precios de jardín
 * cuenta como "sin tocar" (no hay nada que respetar).
 */
export const isGardenPriceRangeFromDiscount = (poolPriceRange, gardenPriceRange, percent) => {
  if (!Array.isArray(gardenPriceRange) || gardenPriceRange.length === 0) {
    return true;
  }
  const expected = applyGardenDiscountToPriceRange(poolPriceRange, percent);
  if (!expected || expected.length !== gardenPriceRange.length) {
    return false;
  }
  return expected.every((row, i) => {
    const saved = gardenPriceRange[i] || {};
    return (
      row.quantityRange === saved.quantityRange &&
      row.amount === Number(saved.amount) &&
      row.currency === (saved.currency || 'EUR')
    );
  });
};

/**
 * Completa `gardenPriceRange` al aparecer tramos nuevos en la piscina.
 *
 * Conserva los importes de jardín ya guardados (el anfitrión puede haberlos tocado a
 * mano) y solo rellena los tramos que faltan con el % de descuento configurado.
 * Los tramos de jardín que ya no existen en la piscina se descartan.
 *
 * @returns {Array|null} tramos alineados con la piscina, o null si no hay % usable
 */
export const mergeGardenPriceRangeWithDiscount = (poolPriceRange, gardenPriceRange, percent) => {
  const pct = Number(percent);
  if (!Number.isFinite(pct) || pct < 0 || pct >= 100 || !Array.isArray(poolPriceRange)) {
    return null;
  }
  const existing = new Map(
    (Array.isArray(gardenPriceRange) ? gardenPriceRange : [])
      .filter(row => row && row.quantityRange && row.amount != null)
      .map(row => [row.quantityRange, row])
  );
  const factor = (100 - pct) / 100;
  const next = poolPriceRange
    .filter(row => row && row.quantityRange && row.amount != null)
    .map(row => {
      const saved = existing.get(row.quantityRange);
      if (saved) {
        return {
          quantityRange: row.quantityRange,
          amount: Math.max(0, Math.round(Number(saved.amount))),
          currency: saved.currency || row.currency || 'EUR',
        };
      }
      return {
        quantityRange: row.quantityRange,
        amount: Math.max(0, Math.round(Number(row.amount) * factor)),
        currency: row.currency || 'EUR',
      };
    });
  return next.length > 0 ? next : null;
};

/**
 * Cara inicial de las sub-pestañas del asistente, leída de `?cara=jardin`.
 */
export const initialEditFaceFromSearch = (locationSearch, { gardenEnabled } = {}) => {
  if (!gardenEnabled) {
    return POOL_FACE;
  }
  const cara = locationSearch?.cara;
  return cara === 'jardin' || cara === 'garden' || cara === 'jardín' ? GARDEN_FACE : POOL_FACE;
};

/**
 * Flujo guiado al activar la cara de jardín: Calendario → Detalles (jardín) → Fotos (jardín).
 * Se marca en la URL (`?cara=jardin&flujo=jardin`) para que cada paso sepa que viene de ahí y
 * enseñe su aviso y su "Guardar y continuar". Ver docs/listing-faces.md.
 */
export const GARDEN_ACTIVATION_FLOW_SEARCH = { cara: 'jardin', flujo: 'jardin' };

export const isGardenActivationFlow = locationSearch => locationSearch?.flujo === 'jardin';

/** Valor del parámetro de URL que fuerza una cara (`?espacio=jardin`). */
export const FACE_QUERY_PARAM = 'espacio';

const GARDEN_PARAM_VALUES = ['jardin', 'jardín', 'jardines', 'garden'];
const POOL_PARAM_VALUES = ['piscina', 'piscinas', 'pool'];

const publicDataOf = listing => listing?.attributes?.publicData || {};

/** Id estable de una imagen ya subida (`imageId` en las recién subidas). */
export const listingImageUuid = image => image?.imageId?.uuid || image?.id?.uuid || null;

/**
 * Reparte las imágenes del anuncio en visibles (en el orden guardado) y ocultas.
 * Sin orden guardado, se muestran todas en el orden de Sharetribe.
 */
export const splitImagesByStoredOrder = (images, storedOrder) => {
  const available = (images || []).filter(image => listingImageUuid(image));
  if (!Array.isArray(storedOrder) || storedOrder.length === 0) {
    return { visible: available, hidden: [] };
  }
  const byId = new Map(available.map(image => [listingImageUuid(image), image]));
  const visible = storedOrder.map(id => byId.get(String(id))).filter(Boolean);
  if (visible.length === 0) {
    return { visible: available, hidden: [] };
  }
  const visibleIds = new Set(visible.map(listingImageUuid));
  return {
    visible,
    hidden: available.filter(image => !visibleIds.has(listingImageUuid(image))),
  };
};

/**
 * Al guardar la cara de jardín no se reescribe el orden de Sharetribe: ese orden es el
 * de la cara base. Se conservan las fotos que siguen en el formulario (en el orden
 * del anuncio), se añaden al final las nuevas y se dejan fuera las borradas.
 */
export const mergeListingImagesForSave = (listingImages, formImages) => {
  const remaining = new Map();
  (formImages || []).forEach(image => {
    const id = listingImageUuid(image);
    if (id) {
      remaining.set(id, image);
    }
  });
  const kept = [];
  (listingImages || []).forEach(image => {
    const id = listingImageUuid(image);
    if (id && remaining.has(id)) {
      kept.push(remaining.get(id));
      remaining.delete(id);
    }
  });
  return [...kept, ...remaining.values()];
};

/**
 * Tipo de espacio elegido por el anfitrión. `category` es la clave antigua, que sigue
 * viva en anuncios anteriores al campo de Console.
 */
export const spaceTypeOf = listing => {
  const publicData = publicDataOf(listing);
  return publicData.searchListingType || publicData.category || null;
};

/** Los datos base del anuncio son los del jardín (anuncio de jardín "de los de antes"). */
export const isGardenSpaceType = listing => spaceTypeOf(listing) === GARDEN;

/**
 * ¿El anfitrión activó la cara de jardín?
 * Acepta el booleano de Flex y el string `"true"` por si llega sin tipar.
 */
export const isHasGardenEnabled = value => value === true || value === 'true';

/**
 * ¿Ha apagado ops la cara piscina? `poolOffered: false` lo escribe el dashboard de
 * migración al fusionar una piscina cerrada con un jardín abierto; se reactiva a mano
 * (o el cron de reapertura, si había fecha). Solo cuenta con el jardín activo: un
 * anuncio no puede quedarse sin ninguna cara.
 */
export const isPoolOfferedDisabled = value => value === false || value === 'false';

/**
 * Qué caras ofrece un anuncio.
 *
 * @param {Object} listing entidad de listing (con `attributes.publicData`)
 * @returns {{ hasPool: boolean, hasGarden: boolean, both: boolean }}
 */
export const listingFaces = listing => {
  if (!listing) {
    return { hasPool: false, hasGarden: false, both: false };
  }
  if (isGardenSpaceType(listing)) {
    return { hasPool: false, hasGarden: true, both: false };
  }
  const publicData = publicDataOf(listing);
  const hasGarden = isHasGardenEnabled(publicData.hasGarden);
  const hasPool = !(hasGarden && isPoolOfferedDisabled(publicData.poolOffered));
  return { hasPool, hasGarden, both: hasPool && hasGarden };
};

/**
 * Anuncio de piscina publicado: candidato a tener cara jardín (sin mirar quién edita).
 */
export const isPoolListingEligibleForGardenFace = listing => {
  const isPublished = !!listing?.id && listing?.attributes?.state !== LISTING_STATE_DRAFT;
  return isPublished && spaceTypeOf(listing) === POOL;
};

/**
 * ¿Se ofrece la sub-pestaña / activador de jardín al editar este anuncio?
 *
 * En anuncios de piscina ya publicados, para anfitriones y operadores. Los
 * borradores y los jardines/interiores nativos no la ven (crear anuncio no
 * cambia). El segundo argumento se conserva por compatibilidad con callers
 * antiguos y se ignora.
 *
 * @param {object} listing
 * @param {object} [_options]
 */
export const canEditGardenFace = (listing, _options = {}) =>
  isPoolListingEligibleForGardenFace(listing);

/** ¿Se puede reservar este anuncio con esta cara? */
export const listingOffersFace = (listing, face) => {
  const { hasPool, hasGarden } = listingFaces(listing);
  return face === GARDEN_FACE ? hasGarden : hasPool;
};

/**
 * Cara pedida por el parámetro de URL, o `null` si no viene o no se reconoce.
 * Acepta las dos lenguas porque la URL es de cara al usuario y el resto del dominio
 * ya mezcla ambas (`/s/jardines` con `searchListingType: 'garden'`).
 */
export const parseFaceParam = raw => {
  if (raw == null) {
    return null;
  }
  const value = String(raw)
    .trim()
    .toLowerCase();
  if (GARDEN_PARAM_VALUES.includes(value)) {
    return GARDEN_FACE;
  }
  if (POOL_PARAM_VALUES.includes(value)) {
    return POOL_FACE;
  }
  return null;
};

/**
 * Primera fecha `YYYY-MM-DD` del param `dates`, o `null`.
 *
 * En la URL viaja como `2026-12-12,2026-12-12`. Si la clave se duplica, `parse` devuelve
 * un array y `typeof dates === 'string'` lo trataba como "no hay fecha" → piscina.
 */
export const dateYmdFromDatesParam = dates => {
  const value = Array.isArray(dates) ? dates[0] : dates;
  if (value == null || value === '') {
    return null;
  }
  const first = String(value)
    .split(',')[0]
    .trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(first) ? first : null;
};

/**
 * Hoy en Madrid, `YYYY-MM-DD`. Es la fecha que decide la cara (y el orden de Todos)
 * cuando la búsqueda o la ficha no traen ninguna.
 */
export const todayYmd = () => stringifyDateToISO8601(new Date(), 'Europe/Madrid');

/**
 * ¿Cae la fecha dentro de la temporada de piscina?
 *
 * @param {string} ymd fecha `YYYY-MM-DD` (la que viaja en el param `dates`)
 * @returns {boolean} false también cuando la fecha no es parseable
 */
export const isSummerDate = ymd => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').trim());
  if (!match) {
    return false;
  }
  const monthDay = `${match[2]}-${match[3]}`;
  return monthDay >= SUMMER_START_MONTH_DAY && monthDay <= SUMMER_END_MONTH_DAY;
};

/**
 * ¿Toca piscina? Con fecha, la ventana de esa fecha (`isPoolSeasonDate`). Sin fecha
 * decide hoy: con los `poolMonths` del anfitrión si los hay, y si no con la ventana sin
 * fecha (marzo–septiembre), más ancha que la de una fecha concreta.
 *
 * @param {string} [dateYmd] fecha buscada o reservada, `YYYY-MM-DD`; vacía si no hay
 * @param {number[]} [poolMonths] meses de piscina del anuncio; sin ellos, la ventana global
 * @returns {boolean}
 */
export const isPoolSeasonFor = (dateYmd, poolMonths) => {
  if (dateYmd) {
    return isPoolSeasonDate(dateYmd, poolMonths);
  }
  const today = todayYmd();
  if (normalizePoolMonths(poolMonths).length > 0) {
    return isPoolSeasonDate(today, poolMonths);
  }
  const monthDay = today.slice(5);
  return monthDay >= NO_DATE_SEASON_START_MONTH_DAY && monthDay <= SUMMER_END_MONTH_DAY;
};

/**
 * Qué cara mostrar. El parámetro de URL manda sobre la fecha, y sin ninguno de los
 * dos decide la temporada de hoy (`todayYmd`): en octubre un anuncio de piscina con
 * jardín se enseña como jardín en todas partes (búsqueda, mapa, ficha, portada), así
 * que la tarjeta y la ficha a la que lleva coinciden. Un enlace antiguo sin fecha abre
 * el jardín en invierno.
 *
 * Con fecha, manda la de la reserva (la del formulario o el param `dates`), no "hoy".
 * Un 12 de diciembre en el panel tiene que mostrar el jardín aunque hoy sea septiembre.
 *
 * Una cara que el anuncio no ofrece se ignora en silencio (un `?espacio=jardin` en un
 * anuncio sin jardín no debe dejar la ficha vacía).
 *
 * @param {Object} params
 * @param {Object} params.listing
 * @param {string} [params.faceParam] valor crudo del param `espacio`
 * @param {string} [params.dateYmd] fecha buscada, `YYYY-MM-DD`
 * @returns {string} `POOL_FACE` | `GARDEN_FACE`
 */
export const resolveListingFace = ({ listing, faceParam, dateYmd } = {}) => {
  const { hasPool, hasGarden } = listingFaces(listing);
  if (!hasGarden) {
    return POOL_FACE;
  }
  if (!hasPool) {
    return GARDEN_FACE;
  }

  const requested = parseFaceParam(faceParam);
  if (requested) {
    return requested;
  }
  return isPoolSeasonFor(dateYmd, publicDataOf(listing).poolMonths) ? POOL_FACE : GARDEN_FACE;
};

/**
 * Subconjunto y orden de fotos sobre la lista única de Sharetribe.
 *
 * Un orden vacío o con ids que ya no existen no puede dejar la galería en blanco, así
 * que en ese caso se devuelven todas. Lo usa `gardenImageOrder` (cara de jardín).
 */
const orderedImages = (listing, order) => {
  const images = Array.isArray(listing?.images) ? listing.images : [];
  if (!Array.isArray(order) || order.length === 0 || images.length === 0) {
    return images;
  }
  const { visible } = splitImagesByStoredOrder(images, order);
  return visible.length > 0 ? visible : images;
};

/**
 * Fotos de la cara base: la lista de Sharetribe menos las que el anfitrión ocultó.
 * Las ids ocultas viven en `hiddenImageIds` (no en un orden de visibles) para que una
 * foto recién subida se vea sin tener que guardar otra vez el paso de fotos.
 */
const imagesWithoutHidden = (listing, hiddenIds) => {
  const images = Array.isArray(listing?.images) ? listing.images : [];
  if (!Array.isArray(hiddenIds) || hiddenIds.length === 0 || images.length === 0) {
    return images;
  }
  const hiddenSet = new Set(hiddenIds.map(String));
  const visible = images.filter(img => img?.id?.uuid && !hiddenSet.has(img.id.uuid));
  return visible.length > 0 ? visible : images;
};

/**
 * Los datos de presentación de una cara, ya resueltos con sus fallbacks.
 *
 * @param {Object} listing
 * @param {string} face `POOL_FACE` | `GARDEN_FACE`
 * @returns {{ face: string, title: string, description: string, priceRange: Array, images: Array }}
 */
export const faceView = (listing, face) => {
  const { title = '', description = '' } = listing?.attributes || {};
  const publicData = publicDataOf(listing);
  const priceRange = Array.isArray(publicData.priceRange) ? publicData.priceRange : [];

  const isGardenFace = face === GARDEN_FACE && !isGardenSpaceType(listing);
  if (!isGardenFace) {
    return {
      face: face === GARDEN_FACE ? GARDEN_FACE : POOL_FACE,
      title,
      description,
      priceRange,
      images: imagesWithoutHidden(listing, publicData.hiddenImageIds),
    };
  }

  const gardenPriceRange = Array.isArray(publicData.gardenPriceRange)
    ? publicData.gardenPriceRange.filter(row => row && row.quantityRange)
    : [];

  return {
    face: GARDEN_FACE,
    title: publicData.gardenTitle || title,
    description: publicData.gardenDescription || description,
    priceRange: gardenPriceRange.length > 0 ? gardenPriceRange : priceRange,
    images: orderedImages(listing, publicData.gardenImageOrder),
  };
};

/**
 * Cara que se reservó (o se está reservando). Ausente o desconocida → piscina,
 * igual que todas las reservas anteriores a esta feature.
 *
 * @param {*} bookingFace valor de `orderData.bookingFace` o `protectedData.bookingFace`
 * @returns {string} `POOL_FACE` | `GARDEN_FACE`
 */
export const resolveBookedFace = bookingFace =>
  bookingFace === GARDEN_FACE ? GARDEN_FACE : POOL_FACE;

/**
 * Presentación (título, fotos…) de la cara que se reservó.
 */
export const bookedFaceView = (listing, bookingFace) =>
  faceView(listing, resolveBookedFace(bookingFace));

/**
 * Cara de una transacción. Manda `protectedData.bookingFace`. Una consulta sin ella (las
 * enviadas antes de que la consulta la guardara) la deduce de la fecha del baño: es la
 * misma regla con la que `BookingTimeForm` la resolverá al pagarla, así que título,
 * desglose y cobro coinciden. Cualquier otra transacción sin cara se cobró como piscina,
 * y también se quedan en piscina las reservas por fuera, que viven en `inquire` pero no
 * pasaron por el formulario.
 *
 * @param {Object} transaction con `attributes.protectedData` y `attributes.metadata`
 * @param {Object} listing
 * @returns {string} `POOL_FACE` | `GARDEN_FACE`
 */
export const transactionFace = (transaction, listing) => {
  const { protectedData, metadata, lastTransition } = transaction?.attributes || {};
  const { bookingFace, bookingStartTime } = protectedData || {};
  if (bookingFace) {
    return resolveBookedFace(bookingFace);
  }
  const isInquiry = lastTransition === 'transition/inquire';
  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  if (!isInquiry || !bookingStartTime || !timeZone || isManualReservation(metadata)) {
    return POOL_FACE;
  }
  const dateYmd = stringifyDateToISO8601(timestampToDate(bookingStartTime), timeZone);
  return resolveListingFace({ listing, dateYmd });
};

/**
 * Query string para abrir la ficha en la cara reservada (`?espacio=jardin`).
 * Piscina no añade param: es el default.
 */
export const listingFaceToSearch = face =>
  resolveBookedFace(face) === GARDEN_FACE ? { search: `?${FACE_QUERY_PARAM}=jardin` } : undefined;

/**
 * Proyecta una cara sobre la entidad de listing, para poder pasarla a componentes que
 * leen `title`, `publicData.priceRange` e `images` del sitio de siempre.
 *
 * Solo sustituye lo que la cara cambia: el resto del anuncio (autor, ubicación, tipo,
 * descuentos) es común a ambas caras y se conserva tal cual.
 */
export const applyFaceToListing = (listing, face) => {
  if (!listing) {
    return listing;
  }
  const resolvedFace = face === GARDEN_FACE ? GARDEN_FACE : POOL_FACE;
  const view = faceView(listing, resolvedFace);
  const imagesChanged = view.images !== listing.images;
  const isGardenOverlay = resolvedFace === GARDEN_FACE && !isGardenSpaceType(listing);

  if (!isGardenOverlay) {
    return imagesChanged ? { ...listing, images: view.images } : listing;
  }

  return {
    ...listing,
    attributes: {
      ...listing.attributes,
      title: view.title,
      description: view.description,
      publicData: { ...publicDataOf(listing), priceRange: view.priceRange },
    },
    images: view.images,
  };
};

/**
 * Qué le pide la URL de búsqueda a `resolveListingFace`.
 *
 * No se pre-resuelve la cara y se vuelve a meter como `faceParam`: `pool`/`garden` son
 * valores válidos de `?espacio=`, y una fecha mal leída se convertiría en "el usuario
 * ha pedido piscina" y ya no se miraría diciembre. La pestaña `/s` no es una petición
 * de piscina; solo `/s/jardines` y `?espacio=` lo son.
 *
 * @param {{ pathname?: string, search?: string }} location
 * @returns {{ faceParam?: string, dateYmd?: string }}
 */
export const listingFaceRequestFromLocation = ({ pathname = '', search = '' } = {}) => {
  const params = parse(search);
  const rawFace = params[FACE_QUERY_PARAM];
  const path = String(pathname);
  const faceParam =
    rawFace != null && String(rawFace).trim() !== ''
      ? rawFace
      : path.startsWith('/s/jardines')
      ? 'jardin'
      : path === '/s' || path === '/s/'
      ? 'piscina'
      : undefined;
  return { faceParam, dateYmd: dateYmdFromDatesParam(params.dates) };
};

/**
 * Cara que toca enseñar en una tarjeta de resultados, leída de la URL de búsqueda.
 *
 * Las tarjetas no reciben la cara por props porque `SearchMapInfoCard` cuelga de una
 * cadena larga de componentes de mapa; la URL es el estado que todas comparten. Fuera
 * de la búsqueda (portada, favoritos) no hay ni pestaña ni fecha y decide la temporada
 * de hoy, como en `resolveListingFace`.
 *
 * La pestaña de jardines pesa como el parámetro explícito: si el usuario ha pedido
 * jardines, un anuncio con las dos caras se enseña ahí como jardín aunque sea verano.
 *
 * @param {{ pathname?: string, search?: string }} location
 * @returns {string} `POOL_FACE` | `GARDEN_FACE`
 */
export const faceFromSearchLocation = location => {
  const { faceParam, dateYmd } = listingFaceRequestFromLocation(location);
  const requested = parseFaceParam(faceParam);
  if (requested) {
    return requested;
  }
  return isPoolSeasonFor(dateYmd) ? POOL_FACE : GARDEN_FACE;
};

/**
 * ¿Puede esta reserva añadir el extra "uso de piscina"? Solo al reservar el jardín de
 * un anuncio que además es piscina y cuyo anfitrión lo ofrece. El importe lo calcula
 * el servidor (server/api-util/listingFaces.js): es la diferencia entre ambas caras y
 * no puede venir del cliente.
 */
export const offersPoolUseExtra = (listing, face) => {
  const { both } = listingFaces(listing);
  return both && face === GARDEN_FACE && publicDataOf(listing).gardenPoolUseExtra === true;
};

/**
 * `?espacio=` para el enlace de una tarjeta o del mapa a la ficha.
 *
 * Solo se fija la cara cuando la búsqueda la pidió de forma explícita (la pestaña de
 * jardines o un `?espacio=` en la URL): el parámetro **fuerza** la cara en la ficha, y
 * forzarla por una cara que solo se dedujo de la fecha dejaría la ficha clavada cuando
 * el bañista cambia esa fecha. La fecha viaja sola en el prefill, así que la ficha la
 * vuelve a resolver igual que la tarjeta.
 *
 * @param {string} face cara que enseña la tarjeta
 * @param {{ faceParam?: string }} [faceRequest] de `listingFaceRequestFromLocation`
 * @returns {Object} `{ espacio }` o `{}`
 */
export const faceLinkQuery = (face, { faceParam } = {}) => {
  const requested = parseFaceParam(faceParam);
  if (!requested) {
    return {};
  }
  return { [FACE_QUERY_PARAM]: face === GARDEN_FACE ? 'jardin' : 'piscina' };
};

/**
 * ¿Hay que avisar de que esta reserva de jardín NO incluye la piscina? Es el caso de un
 * anuncio de piscina con cara de jardín que no ofrece el extra "uso de piscina": el
 * anfitrión lo tiene desactivado, o ops apagó la cara piscina (`poolOffered: false`).
 * Un jardín "de los de antes" no tiene piscina que aclarar, así que no avisa.
 */
export const gardenBookingExcludesPool = (listing, face) =>
  face === GARDEN_FACE &&
  !isGardenSpaceType(listing) &&
  isHasGardenEnabled(publicDataOf(listing).hasGarden) &&
  !offersPoolUseExtra(listing, face);

/**
 * Qué pasos del jardín faltan por rellenar en el asistente.
 *
 * Solo aplica cuando el anuncio puede editar la cara jardín y tiene `hasGarden`.
 * Detalles cuenta como pendiente si no hay título ni descripción propios (ambos
 * caen a la piscina); precio y fotos, si no hay `gardenPriceRange` /
 * `gardenImageOrder`.
 *
 * @param {object} listing
 * @param {object} [_options] ignorado (compat)
 * @returns {{ details: boolean, pricing: boolean, photos: boolean, any: boolean }}
 */
export const gardenFacePendingSections = (listing, _options = {}) => {
  const none = { details: false, pricing: false, photos: false, any: false };
  if (!canEditGardenFace(listing) || !isHasGardenEnabled(publicDataOf(listing).hasGarden)) {
    return none;
  }
  const pd = publicDataOf(listing);
  const details =
    !String(pd.gardenTitle || '').trim() && !String(pd.gardenDescription || '').trim();
  const pricing = !Array.isArray(pd.gardenPriceRange) || pd.gardenPriceRange.length === 0;
  const photos = !Array.isArray(pd.gardenImageOrder) || pd.gardenImageOrder.length === 0;
  return { details, pricing, photos, any: details || pricing || photos };
};

// `closeReason.reasonKey` the face-merge ops tool writes when it closes a legacy garden
// listing merged into a pool listing (scripts/migrate-listing-face-pair.js).
const FACE_MERGE_CLOSE_REASON_KEY = 'face_merge_migrated';

/**
 * Id of the pool listing a closed legacy garden was merged into, or null.
 *
 * A closed listing normally sends visitors to a city search (`closedListingSearchString`).
 * A garden merged into a pool listing is not gone, though: the same space now lives as
 * the garden face of the pool listing. Bañistas with a booking on the old garden
 * (closing does not cancel it), shared links and search engines should land there.
 *
 * Two signals, both written by the face-merge ops tool:
 * - `metadata.faceMergedInto`: set when merging a pair AT THE SAME ADDRESS, whether the
 *   garden is open or was already closed by its host (whose `closeReason` is kept).
 * - `metadata.closeReason.reasonKey === 'face_merge_migrated'`: the tool closed it.
 *
 * @param {Object} listing listing entity, with `metadata.faceMergedInto` / `closeReason`
 * @returns {string|null}
 */
export const faceMergedIntoListingId = listing => {
  const metadata = listing?.attributes?.metadata || {};
  if (typeof metadata.faceMergedInto === 'string' && metadata.faceMergedInto) {
    return metadata.faceMergedInto;
  }
  const closeReason = metadata.closeReason;
  return closeReason?.reasonKey === FACE_MERGE_CLOSE_REASON_KEY &&
    typeof closeReason.sourcePoolListingId === 'string'
    ? closeReason.sourcePoolListingId
    : null;
};

/**
 * Anuncio de jardín antiguo retirado por la migración a caras: cerrado y fusionado en
 * un anuncio de piscina, que es donde vive ahora ese jardín. Se oculta de "Mis anuncios"
 * y no se puede editar, porque el anfitrión que lo reabre duplica el mismo espacio.
 *
 * Solo cerrado: un jardín fusionado que el anfitrión ya reabrió sigue siendo suyo y
 * visible, o no tendría forma de cerrarlo.
 *
 * @param {Object} listing entidad de listing u ownListing, con `metadata`
 * @returns {boolean}
 */
export const isFaceMergeRetiredListing = listing =>
  listing?.attributes?.state === LISTING_STATE_CLOSED && !!faceMergedIntoListingId(listing);
